import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { EditionScreenConfiguration, CommonStyle } from '@luckycart/xpn-types';

import { HomeScreen } from '../templates/screens/HomeScreen';
import { GameScreen } from '../templates/screens/GameScreen';
import { ExpiredScreen } from '../templates/screens/ExpiredScreen';
import { AlreadyPlayedScreen } from '../templates/screens/AlreadyPlayedScreen';
import { WonScreen } from '../templates/screens/WonScreen';
import { LostScreen } from '../templates/screens/LostScreen';
import { NextScreen } from '../templates/screens/NextScreen';
import { CollectScreen } from '../templates/screens/CollectScreen';
import { UnavailableScreen } from '../templates/screens/UnavailableScreen';
import { BlockInfoType } from '../common-types/BlockInfo.type';
import { ScreenTypes } from '../utils/ScreensList';
import { AvailableTags, TagBag } from '../utils/Tags';
import { MultiGameScreen } from '../templates/screens/MultiGameScreen';
import { SingleGameScreen } from '../templates/screens/SingleGameScreen';
import { StyleContext } from '../utils/contexts/style.context';
import { apiServiceInstance } from '../utils/services/ApiService';
import { getIn } from 'formik';


export const Edition: React.FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const { screenType } = useParams<{ screenType: string | undefined }>();
  const [
    displayScreenTypeError,
    setDisplayScreenTypeError,
  ] = useState<boolean>(false);
  const [
    editableBlocks,
    setEditableBlocks,
  ] = useState<BlockInfoType[]>([]);
  const [
    configurations,
    setConfigurations,
  ] = useState<EditionScreenConfiguration | null>(null);
  const [
    style,
    setStyle,
  ] = useState<CommonStyle | null>(null);
  const [
    operation,
    setOperation,
  ] = useState(null);

  useEffect(
    () => {
      window.onmessage = (event): void => {
        if (event.data.type === 'setFullConfigs') {
          setConfigurations(event.data.configs.screens);
          setOperation(event.data.operation);
          if (event.data.configs.commons !== undefined) {
            setStyle(event.data.configs.commons.style);
          }
        } else if (event.data.type === 'setDeltaConfigs') {
          setConfigurations((previous) => {
            if (previous === null) {
              return previous;
            }
            for (const [key, value] of Object.entries(event.data.configs)) {
              //  As the whole config path is sent (with 'screens' prefix) - remove prefix
              const parts = key.replace(/^screens\./u, '').split('.');
              // Use object Ref to set value / custom deepAssign - DO NOT DO THAT AT HOME KIDS !
              parts.reduce((prev, curr, index) => {
                if (prev !== null) {
                  if (index === parts.length - 1) {
                    prev[curr] = value;
                  }
                  return prev[curr] as unknown as Record<string, unknown>;
                }
                return null as unknown as Record<string, unknown>;
              }, previous as unknown as Record<string, unknown>);
            }
            // break ref with new object to trigger refresh
            return { ...previous };
          });

        }
      };
    },
    []
  );


  useEffect(() => {
    const siteKey = searchParams.get('siteKey');
    if (siteKey !== null) {
      apiServiceInstance.fetchSiteData(siteKey).then((data) => {
        if (style === null) {
          setStyle(data.config.commons.style);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessageToPmp = (message: Record<string, unknown>): void => {
    if (window.top) {
      window.top.postMessage(message, '*');
    }
  };

  useEffect(
    () => {
      sendMessageToPmp({
        blocks: editableBlocks,
        type: 'editableBlocks',
      });
    },
    [editableBlocks, editableBlocks.length]
  );
  useEffect(
    () => {
      // Only send object like { [TAG_KEY] : { section: string }  } to lighten the message
      const tagsContext = { operation };
      const tags: TagBag = AvailableTags.filter((tagData) => {
        const screenMatch = tagData.tag.screens.includes(screenType as ScreenTypes);
        if (screenMatch === false) {
          return false;
        }
        if (tagData.tag.conditions !== undefined) {
          const conditionsAreValidated = Object.entries(tagData.tag.conditions).some(([conditionKey, conditionValue]) => {
            const contextValue = getIn(tagsContext, conditionKey);
            return contextValue === conditionValue;
          });
          if (conditionsAreValidated === false) {
            return false;
          }
        }
        return true;
      });
      sendMessageToPmp({
        tags,
        type: 'availableTags',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operation]
  );

  useEffect(
    () => {
      setDisplayScreenTypeError(screenType !== undefined && !Object.values<string>(ScreenTypes).includes(screenType));
      setEditableBlocks([]);
    },
    [screenType]
  );

  if (configurations === null) {
    return <></>;
  }

  if (displayScreenTypeError) {
    return <>Screen type is not recognised.</>;
  }

  const addEditableBlock = (block: BlockInfoType): void => {
    setEditableBlocks((previous: BlockInfoType[]) => previous.concat(block));
  };

  const removeEditableBlock = (configPath: string): void => {
    setEditableBlocks((previous: BlockInfoType[]) => previous.filter((block) => block.configPath !== configPath));
  };

  const onBlockEdit = (event: React.MouseEvent<HTMLDivElement>): void => {
    const selectBlock = {
      blockType: event.currentTarget.getAttribute('data-template-type'),
      configPath: event.currentTarget.getAttribute('data-config-path'),
      label: event.currentTarget.getAttribute('data-display-name'),
      sizeInfo: {
        height: event.currentTarget.getAttribute('data-size-height'),
        width: event.currentTarget.getAttribute('data-size-width'),
      },
    };
    sendMessageToPmp({
      block: selectBlock,
      type: 'selectBlock',
    });
    event.stopPropagation();
  };

  const commonProps = {
    suOptinForm: false,
    siteKey: searchParams.get('siteKey'),
    addEditableBlock,
    removeEditableBlock,
    isEdition: true,
    onBlockEdit,
  };

  const previewThumbnailList = [
    { campId: 'camp_1', url: '/static-assets/placeholder.png' },
    { campId: 'camp_2', url: '/static-assets/placeholder.png' },
    { campId: 'camp_3', url: '/static-assets/placeholder.png' },
    { campId: 'camp_4', url: '/static-assets/placeholder.png' },
    { campId: 'camp_5', url: '/static-assets/placeholder.png' },
  ];

  return (
    <StyleContext.Provider value={{
      setStyle,
      style,
    }}>
      {
        screenType === ScreenTypes.HOME &&
        <HomeScreen config={configurations.home} {...commonProps} />
      }
      {
        screenType === ScreenTypes.GAME &&
        <GameScreen config={configurations.game} {...commonProps} />
      }
      {
        screenType === ScreenTypes.EXPIRED &&
        <ExpiredScreen config={configurations.expired} {...commonProps} />
      }
      {
        screenType === ScreenTypes.UNAVAILABLE &&
        <UnavailableScreen config={configurations.unavailable} {...commonProps} />
      }
      {
        screenType === ScreenTypes.ALREADY_PLAYED &&
        <AlreadyPlayedScreen config={configurations.alreadyPlayed} {...commonProps} />
      }
      {
        screenType === ScreenTypes.WON &&
        <WonScreen config={configurations.won} {...commonProps} />
      }
      {
        screenType === ScreenTypes.LOST &&
        <LostScreen config={configurations.lost} {...commonProps} />
      }
      {
        screenType === ScreenTypes.NEXT &&
        <NextScreen config={configurations.next} {...commonProps} />
      }
      {
        screenType === ScreenTypes.COLLECT &&
        <CollectScreen config={configurations.collect} {...commonProps} />
      }
      {
        screenType === ScreenTypes.MULTIGAME &&
        <MultiGameScreen config={configurations.multiGame} {...commonProps} thumbnailUrls={previewThumbnailList} />
      }
      {
        screenType === ScreenTypes.SINGLEGAME &&
        <SingleGameScreen config={configurations.singleGame} {...commonProps} thumbnailUrls={previewThumbnailList.slice(-1)} />
      }
    </StyleContext.Provider>
  );
};
